import { PayloadActionCreator } from '@reduxjs/toolkit'

export type HandlerOptions<TPayload> = {
  action: PayloadActionCreator<TPayload>
  onProcess: (payload: TPayload) => void
} & ({
  getKey: (payload: TPayload) => string
  mode?: never
} | {
  mode: 'all' | 'last' | 'first'
  getKey?: never
})

export const createActionHandler = <TPayload>(
  {
    action,
    getKey,
    mode,
    onProcess
  }: HandlerOptions<TPayload>
) => {
  let scheduled = false
  const pending = getKey ? new Map<string, TPayload>() : null

  let queue: TPayload[] = []
  let lastPayload: TPayload | null = null
  let firstPayload: TPayload | null = null

  // флаги введены, т.к. TPayload может быть undefined | null
  let hasLast = false
  let hasFirst = false

  const run = () => {
    if (pending) {
      pending.forEach(onProcess)
      pending.clear()
    } else {
      switch (mode) {
        case 'all':
          queue.forEach(onProcess)
          queue = []
          break
        case 'last':
          if (hasLast) {
            onProcess(lastPayload as TPayload)
            lastPayload = null
            hasLast = false
          }
          break
        case 'first':
          if (hasFirst) {
            onProcess(firstPayload as TPayload)
            firstPayload = null
            hasFirst = false
          }
          break
      }
    }
    scheduled = false
  }

  return (act: unknown) => {
    if (!action.match(act)) return

    const payload = act.payload

    if (pending && getKey) {
      pending.set(getKey(payload), payload)
    } else {
      switch (mode) {
        case 'all':
          queue.push(payload)
          break
        case 'last':
          lastPayload = payload
          hasLast = true
          break
        case 'first':
          if (!hasFirst) {
            firstPayload = payload
            hasFirst = true
          }
          break
      }
    }

    if (!scheduled) {
      scheduled = true
      queueMicrotask(run)
    }
  }
}
